import Typography from '@components/_atoms/Typography';
import { TypoVariant } from '@constants/atoms';
import styled from '@emotion/styled';
import { useIsMobile } from '@hooks/responsive/useIsMobile';
import { gray, primary, red } from '@styles/Colors';
import { Radius } from '@styles/Radius';

// type
import { CompanyRegistrationDataType } from '..';
import FileInputBox from '@components/_molecules/FileInputBox';
import { FileTarget } from '@constants/service';
import { SignUpFormType } from '@components/_templates/auth/SignUpContent/types';
import { Dispatch, SetStateAction } from 'react';

type Props = {
  data: CompanyRegistrationDataType;
  handleResetCompanyRegistrationNumber: () => void;
  disabled: boolean;
  form?: SignUpFormType;
  setForm?: Dispatch<SetStateAction<SignUpFormType>>;
};

const Layout = styled.div`
  padding: 16px;
  border: 1px solid ${gray.gray4};
  border-radius: ${Radius.MEDIUM};

  display: grid;
  row-gap: 16px;
`;

const Title = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Content = styled.div`
  display: grid;
  row-gap: 8px;
`;

export default function CompanyInfoBox({ data, handleResetCompanyRegistrationNumber, disabled, form, setForm }: Props) {
  const isMobile = useIsMobile();

  const { company_name, company_address, company_ceo } = data;

  // 파일 업로드
  const handleFileUpload = (file: File, cdnUrl: string, name: string) => {
    setForm?.((prev) => ({
      ...prev,
      company_address: '',
      company_name: '',
      company_ceo: '',
      company_registration_number: '',
      company_registration_number_file_name: file.name,
      company_registration_number_file: cdnUrl,
    }));
  };

  // 파일 삭제
  const handleFileRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
    setForm?.((prev) => ({
      ...prev,
      company_registration_number_file_name: '',
      company_registration_number_file: '',
    }));
  };

  return (
    <Layout>
      <Title>
        <Typography variant={TypoVariant.SH4} color={primary.gray}>
          사업자 정보
        </Typography>
        <Typography
          style={{ cursor: 'pointer' }}
          variant={TypoVariant.SH4}
          color={disabled ? primary.blue : red.red3}
          onClick={handleResetCompanyRegistrationNumber}>
          다시 인증하기
        </Typography>
      </Title>
      <Content>
        {disabled ? (
          <>
            <Typography variant={isMobile ? TypoVariant.B5 : TypoVariant.B4} color={primary.gray}>
              회사명 : {company_name}
            </Typography>
            <Typography variant={isMobile ? TypoVariant.B5 : TypoVariant.B4} color={primary.gray}>
              주소 : {company_address}
            </Typography>
            <Typography variant={isMobile ? TypoVariant.B5 : TypoVariant.B4} color={primary.gray}>
              대표자 : {company_ceo}
            </Typography>
          </>
        ) : (
          <>
            {/** TODO : 사업자 업로드 임시 */}
            {form && (
              <>
                <Typography variant={isMobile ? TypoVariant.B5 : TypoVariant.B4} color={primary.gray}>
                  사업자 정보를 조회 할 수 없습니다. (정보 조회 서비스의 문제 일 수 있습니다.)
                </Typography>
                <Typography variant={isMobile ? TypoVariant.B5 : TypoVariant.B4} color={primary.blue}>
                  하단 파일 등록 영역을 활용하여 사업자등록증 파일을 업로드 부탁드립니다.
                </Typography>
                <Typography variant={isMobile ? TypoVariant.B5 : TypoVariant.B4} color={primary.gray}>
                  빠르게 확인 후 정보 업데이트 하도록 하겠습니다.
              </Typography>
              </>
            )}
            {!form && (
              <Typography variant={isMobile ? TypoVariant.B5 : TypoVariant.B4} color={primary.gray}>
                사업자등록번호 조회에 오류가 발생했습니다.
                <br />
                정확하지 않거나, 현재 휴/폐업 상태의 사업자 입니다.
                <br />
                사업자등록번호를 다시 입력해주세요.
              </Typography>
            )}

            {form && (
              <FileInputBox
                isDescription={false}
                fileInput={{
                  name: 'company_registration_number_file',
                  cdnKey: form.company_registration_number_file,
                  formFileName: form.company_registration_number_file_name,
                  target: FileTarget.BRN,
                  accept: '.jpg, .png, .jpeg, .pdf',
                  handleFileUpload,
                  handleFileRemove,
                }}
              />
            )}
          </>
        )}
      </Content>
    </Layout>
  );
}
